import React, { Fragment, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

/* Helpers */
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

/* Components */
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbRespPlaceholderTile } from './SbRespPlaceholderTile';

import styles from './sbRespSearchResultContainer.scss';

import { useSearchCorrections } from '../useSearchCorrections';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 2;
const ITEMS_PER_ROW_FOR_SB_TOP_NAV = 3;
const LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV = ITEMS_PER_ROW_FOR_SB_TOP_NAV * 2;

const getItemsOrPlaceholders = ({ items, refetchInFlight, pageSize }) => {
    if (refetchInFlight) {
        const placeholderItem = { node: { item: {} } };
        return Array(pageSize).fill(placeholderItem);
    }
    return items || [];
};

const SbRespSearchResultContainerComponent = props => {
    const {
        currency,
        showMeasurements,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        itemSearch,
        viewer,
        relay,
        isClient,
        user,
    } = props;

    const { edges: items = [], pageType } = itemSearch || {};
    const {
        isTileAdEnabled,
        isFavoritesEnabled,
        isSeoQuicklookEnabled,
        isVisuallySimilarEnabled,
    } = viewer;

    const userId = user ? user.serviceId : null;

    const { isSbTopNavVariant } = useContext(ServerVarsContext);

    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });
    const [measurementUnit] = useMeasurementUnit({ itemSearch, user });
    /* eslint-disable react/prop-types */

    const pageSize = useSelector(state => state.relayVariables.variables?.first);
    const refetchInFlight = useSelector(state => state.filters.refetchInFlight);
    const itemsOrPlaceholders = getItemsOrPlaceholders({ items, refetchInFlight, pageSize });

    return (
        /*
         * ID is added in container div, added for scroll tracking taking place
         * in DBL. Used to trigger user launching a survey modal.
         */
        <div className={styles.container} id="sb-results-container">
            <FavoritesProvider
                disable={!isClient}
                environment={relay.environment}
                itemIds={
                    items ? items.filter(Boolean).map(({ node }) => node?.item?.serviceId) : []
                }
                location={locations.SEARCH_BROWSE}
                userId={userId}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider
                                itemSearch={itemSearch}
                                searchCorrectionTerm={searchCorrectionTerm}
                                viewer={viewer}
                            >
                                {({ fireItemImpressionTracking, fireItemClickTracking }) =>
                                    itemsOrPlaceholders.map(({ node: { item } }, index) => {
                                        const quickViewProps = isSeoQuicklookEnabled
                                            ? quickView
                                            : null;

                                        if (refetchInFlight) {
                                            return <SbRespPlaceholderTile />;
                                        }
                                        return (
                                            <Fragment key={item?.serviceId || index}>
                                                {isTileAdEnabled && (
                                                    <SbSharedProductAdWrapper
                                                        index={index}
                                                        itemSearch={itemSearch}
                                                        user={user}
                                                        viewer={viewer}
                                                    />
                                                )}
                                                <SharedItemTile
                                                    isVisuallySimilarEnabled={
                                                        isVisuallySimilarEnabled &&
                                                        pageType !== pageTypes.EXCLUSIVE
                                                    }
                                                    item={item}
                                                    favorites={isFavoritesEnabled && favorites}
                                                    index={index}
                                                    currency={currency}
                                                    isMobile={false}
                                                    showInternalSellerName={showInternalSellerName}
                                                    isClient={isClient}
                                                    showSellerName={showSellerName}
                                                    showCreatorName={showCreatorName}
                                                    showMeasurements={showMeasurements}
                                                    quickViewProps={quickViewProps}
                                                    lazyLoadStartIndex={
                                                        isSbTopNavVariant
                                                            ? LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV
                                                            : LAZY_LOAD_ITEM_TILE_INDEX
                                                    }
                                                    imageLoadVerticalOffset={
                                                        VERTICAL_OFFSET_SB_RESP_IMG
                                                    }
                                                    onContentVisible={() =>
                                                        fireItemImpressionTracking(index)
                                                    }
                                                    onClick={() => fireItemClickTracking(index)}
                                                    measurementUnit={measurementUnit}
                                                    viewer={viewer}
                                                    itemSearch={itemSearch}
                                                />
                                            </Fragment>
                                        );
                                    })
                                }
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
    /* eslint-enable react/prop-types */
};

SbRespSearchResultContainerComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    showMeasurements: PropTypes.bool.isRequired,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    user: PropTypes.object,
};

export const SbRespSearchResultContainer = createFragmentContainer(
    SbRespSearchResultContainerComponent,
    {
        viewer: graphql`
            fragment SbRespSearchResultContainer_viewer on Viewer {
                isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
                isTileAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFavoritesEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isVisuallySimilarEnabled: isEnabledByLocale(featureName: "VISUALLY_SIMILAR")
                isSbTradeFreeShippingTestDisabled: isEnabled(key: "disableSbTradeFreeShippingTest")
                    @include(if: $isTrade)
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
                ...SbSharedProductAdWrapper_viewer
            }
        `,
        user: graphql`
            fragment SbRespSearchResultContainer_user on User {
                serviceId
                ...useMeasurementUnit_user
                ...SbSharedProductAdWrapper_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
                ...useMeasurementUnit_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                ...useSearchCorrections_itemSearch
                ...SbSharedProductAdWrapper_itemSearch
                ...SharedItemTile_itemSearch
                pageType
                edges {
                    node {
                        item {
                            serviceId
                            ...SharedItemTile_item
                        }
                    }
                }
            }
        `,
    }
);
